import { isSameDay, isWeekend } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { useFormikContext } from "formik";
import Select from "library/Atoms/Select";
import Toggler from "library/Atoms/Switch/Toggler";
import Tooltip from "library/Atoms/Tooltip";
import DatePicker from "library/Molecules/DatePicker/DatePicker";
import { first, last } from "lodash";
import useTimeForm from "pages/NewOrder/hooks/useTimeForm";
import DateFormLayout from "pages/NewOrder/layouts/FormLayout/DateFormLayout/DateFormLayout";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { retrieveContactTimezone } from "store/features/localization.store";
import { formatFullDate } from "utility/date-fns";
import { isPeakTime } from "utility/helpers/general";
import { generateTimePickerData } from "utility/helpers/orders";
import { DEFAULT_REPEAT_ORDER_SETTINGS, REPEAT_ORDER_TYPES } from "../../../../../constants";
import RepeatSettingsForm from "./RepeatSettingsForm/RepeatSettingsForm";

const DateForm = () => {
  const { t } = useTranslation();

  const {
    values: { startDate, endDate, isExpress, repeat_settings },
    setFieldValue,
  } = useFormikContext();

  const [{ timePickUpOptions, timeToDropOffOptions }, { onToggleIsExpress, onChangePickUpAt, onChangeDropOffAt }] =
    useTimeForm();

  const timezone = useSelector(retrieveContactTimezone);

  const onChangeDate = useCallback(
    (newDate) => {
      if (!newDate) {
        setFieldValue("startDate", null);
        setFieldValue("endDate", null);
        setFieldValue("repeat_settings", DEFAULT_REPEAT_ORDER_SETTINGS);
      } else {
        const optionsForTimePicker = generateTimePickerData(newDate, { timezone });

        setFieldValue("startDate", first(optionsForTimePicker).value);
        setFieldValue("endDate", last(optionsForTimePicker).value);

        if (repeat_settings.type !== REPEAT_ORDER_TYPES.NONE) {
          setFieldValue("repeat_settings", { ...repeat_settings, dates: [] });
        }
      }
    },
    [setFieldValue, repeat_settings, timezone]
  );

  return (
    <DateFormLayout
      className="mt-3 pt-2"
      titleSection={t("newOrder.forms.setDeliveryForm.dateForm.formTitle")}
      expressTogglerSection={
        <Toggler
          id="isExpress"
          name="isExpress"
          label={
            <>
              <span>{t("common.forms.expressDeliveryLabel")}</span>
              <Tooltip>{t("common.forms.expressDeliveryTooltip")}</Tooltip>
            </>
          }
          onChange={onToggleIsExpress}
        />
      }
      datePickerSection={
        <DatePicker
          id="deliveryDate"
          name="deliveryDate"
          value={startDate || null}
          label={t("newOrder.forms.setDeliveryForm.dateForm.deliveryDateLabel")}
          placeholder={t("newOrder.forms.setDeliveryForm.dateForm.deliveryDatePlaceholder")}
          helper={
            isWeekend(utcToZonedTime(startDate, timezone)) && (
              <span className="text-color-warning">
                ({t("newOrder.forms.setDeliveryForm.dateForm.deliveryDateWeekendHelper")})
              </span>
            )
          }
          onChange={onChangeDate}
          timezone={timezone}
          required
        />
      }
      startTimeInputSection={
        <Select
          id="inputTimeStart"
          name="startDate"
          label={t("common.forms.pickUpTimeLabel")}
          placeholder={t("common.forms.pickUpTimePlaceholder")}
          value={startDate}
          options={timePickUpOptions}
          onChange={onChangePickUpAt}
          helper={
            startDate && (
              <span className="d-flex flex-wrap">
                <span className="pr-2">{formatFullDate(startDate, timezone)}</span>

                {!isWeekend(utcToZonedTime(startDate, timezone)) && isPeakTime(utcToZonedTime(startDate, timezone)) && (
                  <span className="text-color-warning">({t("common.forms.pickUpTimePeakHelper")})</span>
                )}
              </span>
            )
          }
          disabled={!startDate}
          required
        />
      }
      endTimeInputSection={
        <Select
          id="inputTimeEnd"
          name="endDate"
          label={t("common.forms.dropOffTimeLabel")}
          placeholder={t("common.forms.dropOffTimePlaceholder")}
          value={endDate}
          options={timeToDropOffOptions}
          onChange={onChangeDropOffAt}
          helper={
            endDate && (
              <span className="d-flex flex-wrap">
                <span className="pr-2">{formatFullDate(endDate, timezone)}</span>
                {isExpress && (
                  <span className="text-color-warning">({t("common.forms.dropOffTimeExpressDeliveryHelper")})</span>
                )}
                {!isSameDay(utcToZonedTime(startDate, timezone), utcToZonedTime(endDate, timezone)) && (
                  <span className="text-color-warning">({t("common.forms.dropOffTimeNextDayDeliveryHelper")})</span>
                )}
              </span>
            )
          }
          disabled={!startDate}
          required
        />
      }
      repeatFormSection={<RepeatSettingsForm />}
    />
  );
};

export default DateForm;
